import CreateMenu from './ui/CreateMenu';
import { ScreenBoundary, QuietBoundary } from './ErrorBoundary';
import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './session';
import Tour, { tourSeen, TOUR_EVENT } from './ui/Tour';
import AuthScreen from './screens/AuthScreen';
import TodayScreen from './screens/TodayScreen';
import MonthScreen from './screens/MonthScreen';
import WeekScreen from './screens/WeekScreen';
import YearScreen from './screens/YearScreen';
import TasksScreen from './screens/TasksScreen';
import HobbiesScreen from './screens/HobbiesScreen';
import SettingsScreen from './screens/SettingsScreen';
import PeopleScreen from './screens/PeopleScreen';
import AnswerScreen from './screens/AnswerScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import ThemeToggle from './ui/ThemeToggle';
import PushBanner from './ui/PushBanner';
import VerifyBanner from './ui/VerifyBanner';
import { useRealtime } from './ui/socket';
import { readLocal, writeLocal } from './api';
import Icon from './ui/Icon';
import type { IconName } from './ui/Icon';
import EnvBadge from './ui/EnvBadge';

/* The pages somebody reaches from a link — a booking, a shared schedule, the
   help page, the admin console. They load on their own, so a stranger opening
   a booking link does not download the whole app first, and they are routed
   before the sign-in wall because none of them needs a session to begin. */
const BookPage = lazy(() => import('./public/BookPage'));
const SharePage = lazy(() => import('./public/SharePage'));
const HelpPage = lazy(() => import('./public/HelpPage'));
const AdminPage = lazy(() => import('./public/AdminPage'));

export const isPublicPath = (p: string) => {
  const path = p.replace(/\/+$/, '') || '/';
  return /^\/(book|s)\/./.test(path) || path === '/help' || path === '/admin';
};

/* `full` is what the section is called; `label` is what fits.
 *
 * Two of these screens have names longer than a phone's tab bar can hold at a
 * readable size, and a tab that wraps to two lines is worse than a tab that
 * abbreviates. The visible word stays short and the accessible name is the
 * whole thing — allowed precisely because the visible label is contained in
 * it, so somebody using a screen reader hears the real name and somebody
 * using their eyes reads it at the top of the screen a moment later.
 */
const TABS: { to: string; label: string; full: string; icon: IconName; also: string[] }[] = [
  /* Day, week, month and year are not four sections — they are four distances
     from the same thing, and switching between them is zooming rather than
     navigating. They share one entry here and a switcher at the top of the
     screen, which is where every calendar people already use puts it, and
     which leaves room for Tasks and Balance on a phone. `also` is the rest of
     the family, so the tab stays lit at every distance. */
  { to: '/day', label: 'Calendar', full: 'Calendar — day, week, month and year',
    icon: 'calendar', also: ['/week', '/month', '/year'] },
  { to: '/tasks', label: 'Tasks', full: 'Tasks and appointments', icon: 'tasks', also: [] },
  { to: '/hobbies', label: 'Hobbies', full: 'Hobbies and the time that is yours', icon: 'hobbies', also: [] },
  { to: '/people', label: 'People', full: 'People, sharing and meetings', icon: 'people', also: [] }
];

const SIDEBAR_KEY = 'amon.sidebar';

export default function App() {
  const { user, loading } = useSession();

  /* The live wire. The server has broadcast every change to every signed-in
     device since the beginning and this client listened to none of it, so a day
     planned on a phone did not reach the laptop until something happened to
     refetch it. Hooked here rather than per screen: one socket per session, not
     one per view. */
  useRealtime(!!user);
  /* Which of the calendar's four distances is open, so the one tab that
     represents all of them stays lit. */
  const here = useLocation().pathname;
  const answering = here.startsWith('/answer/');
  /* The tour, on first use only.
   *
   * Gated on a signed-in user rather than on the app having loaded: a tour
   * that runs over the sign-in card narrates a product the person cannot see
   * yet, and the prototype learned to sequence this behind onboarding for the
   * same reason. `null` is "not decided yet", so the first render of a fresh
   * session does not flash it. */
  const [tour, setTour] = useState(false);
  /* Local, and only for the moment between finishing setup and the refreshed
     user arriving — without it the screen flickers back to step one. */
  const [skippedSetup, setSkippedSetup] = useState(false);
  /* The sidebar can fold to icons on a laptop, for somebody who wants the width
     for the week grid. Remembered on this device only: a folded sidebar is how
     you like this screen, not a fact about your account. */
  const [folded, setFolded] = useState(() => readLocal(SIDEBAR_KEY) === 'folded');
  const fold = () => setFolded(f => {
    writeLocal(SIDEBAR_KEY, f ? 'open' : 'folded');
    return !f;
  });

  useEffect(() => {
    if (loading || !user) return;
    if (!user.onboarded && !skippedSetup) return;    // setup first, then the tour
    if (tourSeen()) return;
    /* A beat, so the first screen is painted and settled underneath it. A
       spotlight that lands on an element which then moves is worse than no
       spotlight. */
    const t = setTimeout(() => setTour(true), 900);
    return () => clearTimeout(t);
  }, [loading, user, skippedSetup]);

  /* And on request, for ever after. */
  useEffect(() => {
    const again = () => setTour(true);
    window.addEventListener(TOUR_EVENT, again);
    return () => window.removeEventListener(TOUR_EVENT, again);
  }, []);

  if (isPublicPath(here)) {
    return (
      <Suspense fallback={<div className="boot" role="status"><div className="brand-mark">A</div><p>Loading…</p></div>}>
        <Routes>
          <Route path="/book/cancel/:token" element={<BookPage />} />
          <Route path="/book/:slug" element={<BookPage />} />
          <Route path="/s/:slug" element={<SharePage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/help" replace />} />
        </Routes>
      </Suspense>
    );
  }

  /* Until `/auth/me` settles there is no honest answer to "are you signed in",
     and guessing produces the flash of a sign-in form on every reload for
     somebody who is signed in. */
  if (loading) {
    return (
      <div className="boot" role="status" aria-live="polite">
        <div className="brand-mark">A</div>
        <p>Loading your day…</p>
      </div>
    );
  }

  /* The one screen somebody without an account is meant to reach. Checked
     before the sign-in wall rather than inside it: a poll behind a sign-up is
     a poll with three answers. */
  if (answering) {
    return (
      <Routes>
        <Route path="/answer/:token" element={<AnswerScreen />} />
      </Routes>
    );
  }

  if (!user) return <AuthScreen />;

  /* Before the app, and before the tour: a tour of a day built from defaults
     is a tour of somebody else's life. `onboarded` is the server's flag, so
     this cannot reappear on another device once it is done. */
  if (!user.onboarded && !skippedSetup) {
    return <OnboardingScreen onDone={() => setSkippedSetup(true)} />;
  }

  return (
    <div className={`shell${folded ? ' is-folded' : ''}`}>
      <a className="skip-link" href="#main">Skip to content</a>
      <EnvBadge />
      <main className="view" id="main" tabIndex={-1}>
        {/* Above the screens rather than inside one: a person who cannot be
            notified needs to know that wherever they are, and the answer does
            not belong to the day view any more than to the month. */}
        {/* Not on Settings, which has the same news in its Reminders section
            with the controls to act on it — the same sentence twice, one
            above the other, reads as a glitch. */}
        <VerifyBanner />
        {here !== '/settings' && <PushBanner signedIn={!!user} />}
        {/* A crash in one screen stays in that screen: the sidebar, New and
            every other screen keep working, and navigating clears it. */}
        <ScreenBoundary resetKey={here}>
        <Routes>
          <Route path="/day" element={<TodayScreen />} />
          <Route path="/week" element={<WeekScreen />} />
          <Route path="/month" element={<MonthScreen />} />
          <Route path="/year" element={<YearScreen />} />
          <Route path="/tasks" element={<TasksScreen />} />
          <Route path="/hobbies" element={<HobbiesScreen />} />
          {/* The old address. Balance was the wrong name for it: the screen is
              about hobbies, and "balance" is what having them produces. */}
          <Route path="/balance" element={<Navigate to="/hobbies" replace />} />
          <Route path="/people" element={<PeopleScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          {/* The old address. Somebody's bookmark is not a 404. */}
          <Route path="/today" element={<Navigate to="/day" replace />} />
          {/* Anything else is not a 404 worth showing somebody — it is almost
              always a stale bookmark or a hand-typed path. */}
          <Route path="*" element={<Navigate to="/day" replace />} />
        </Routes>
        </ScreenBoundary>
      </main>

      {tour && <Tour onClose={() => setTour(false)} />}

      {/* On a phone the sidebar is a thumb bar with no room for a button, so
          the same menu floats above it, where a thumb already is. */}
      <QuietBoundary name="create-menu"><CreateMenu variant="fab" /></QuietBoundary>

      {/* One navigation, two shapes: a sidebar with words on a screen wide
          enough for them, a bar of icons with short labels under a thumb on a
          phone. The same links in both — CSS decides the shape, so there is
          no second menu to keep in step with the first.

          The theme control sits beside the navigation, not inside it: a
          landmark called "Sections" that contains a button which is not a
          section is a small lie, and screen-reader users are the ones who
          hear it. */}
      <div className="tabbar">
        <div className="tabbar-brand">
          <span className="brand-mark" aria-hidden="true">A</span>
          <span className="tabbar-brand-name" aria-hidden="true">Amon</span>
          <button type="button" className="btn btn-ghost btn-icon sidebar-fold"
                  onClick={fold} aria-pressed={folded}
                  aria-label={folded ? 'Expand the sidebar' : 'Collapse the sidebar'}
                  title={folded ? 'Expand the sidebar' : 'Collapse the sidebar'}>
            <Icon name="sidebar" size={18} />
          </button>
        </div>
        <QuietBoundary name="create-menu"><CreateMenu variant="sidebar" /></QuietBoundary>
        <nav className="tabs" aria-label="Sections">
          {TABS.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              aria-label={t.full === t.label ? undefined : t.full}
              title={t.full}
              className={({ isActive }) =>
                `tab${isActive || t.also.includes(here) ? ' is-active' : ''}`}>
              <Icon name={t.icon} size={20} />
              <span className="tab-label">{t.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="tabbar-end">
          {/* Who you are and where your settings are, as one thing — the way
              every app people already use puts the account at the foot of
              the sidebar. The accessible name stays "Settings…" because that
              is where it goes. */}
          <NavLink to="/settings"
                   className={({ isActive }) => `user-chip${isActive ? ' is-active' : ''}`}
                   aria-label="Settings, devices and reminders" title="Settings">
            <span className="user-avatar" aria-hidden="true">
              {(user.name || user.email || '?').trim().charAt(0).toUpperCase()}
            </span>
            <span className="user-meta" aria-hidden="true">
              <strong>{user.name || user.email.split('@')[0]}</strong>
              <span className="muted small">Settings</span>
            </span>
            <Icon name="settings" size={17} className="icon user-gear" />
          </NavLink>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}
